import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Accessible dialog behaviour: locks page scroll, moves focus inside, keeps Tab within
// the dialog, closes on Escape and gives focus back to whatever opened it.
export const useDialog = (
    open: boolean,
    onClose: () => void,
    containerRef: RefObject<HTMLElement | null>
): void => {
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) return

        const container = containerRef.current
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'

        const focusables = (): HTMLElement[] => container
            ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.getClientRects().length > 0)
            : []

        const frame = window.requestAnimationFrame(() => {
            const preferred = container?.querySelector<HTMLElement>('[data-autofocus]')
            const target = preferred ?? focusables()[0] ?? container

            target?.focus()
        })

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onCloseRef.current()
                return
            }

            if (event.key !== 'Tab' || !container) return

            const elements = focusables()

            if (elements.length === 0) {
                event.preventDefault()
                return
            }

            const first = elements[0]
            const last = elements[elements.length - 1]
            const active = document.activeElement

            if (!container.contains(active)) {
                event.preventDefault()
                first.focus()
            }
            else if (event.shiftKey && active === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && active === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            window.cancelAnimationFrame(frame)
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = previousOverflow
            previouslyFocused?.focus()
        }
    }, [open, containerRef])
}
